/**
 * ScannerModal - Barcode scanner modal for mobile emulator
 */
import React from 'react';
import { Scan, Trash2 } from 'lucide-react';
import type { ItemContado } from '../../hooks/useMobileScanner';

interface ScannerModalProps {
  codigoBarras: string;
  ultimaLeitura: string;
  feedbackLeitura: boolean;
  itensEscaneados: ItemContado[];
  error: string;
  codigoBarrasInputRef: React.RefObject<HTMLInputElement>;
  onCodigoBarrasChange: (value: string) => void;
  onBuscarProduto: (codigo: string) => void;
  onSimularLeitura: () => void;
  onEditarQuantidade: (id: string, quantidade: number) => void;
  onRemoverItem: (id: string) => void;
  onConfirmarItens: () => void;
  onClose: () => void;
}

export const ScannerModal: React.FC<ScannerModalProps> = ({
  codigoBarras,
  ultimaLeitura,
  feedbackLeitura,
  itensEscaneados,
  error,
  codigoBarrasInputRef,
  onCodigoBarrasChange,
  onBuscarProduto,
  onSimularLeitura,
  onEditarQuantidade,
  onRemoverItem,
  onConfirmarItens,
  onClose,
}) => {
  return (
    <div className="absolute inset-0 bg-yellow-100 z-10 flex flex-col p-6 overflow-y-auto">
      <div className="flex-1 flex flex-col justify-center">
        {/* Scanner icon with feedback */}
        <div className={`transition-all duration-300 text-center mb-4 ${feedbackLeitura ? 'scale-110' : 'scale-100'}`}>
          <Scan className={`w-16 h-16 mx-auto ${feedbackLeitura ? 'text-green-600 animate-pulse' : 'text-yellow-700'}`} />
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">Scanner Ativo</h3>
        <p className="text-sm text-gray-600 mb-6 text-center">Escaneie produtos e confirme ao finalizar</p>

        {/* Error message */}
        {error && (
          <div className="bg-red-100 border-2 border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-800 text-sm font-medium text-center">⚠️ {error}</p>
          </div>
        )}

        {/* Barcode input */}
        <div className="bg-white rounded-lg p-4 mb-4 border-2 border-gray-300">
          <label className="block text-xs font-semibold text-gray-600 mb-2">CÓDIGO DE BARRAS</label>
          <input
            ref={codigoBarrasInputRef}
            type="text"
            value={codigoBarras}
            onChange={(e) => onCodigoBarrasChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && codigoBarras.trim()) {
                e.preventDefault();
                onBuscarProduto(codigoBarras.trim());
              }
            }}
            placeholder="Digite ou escaneie o código..."
            autoFocus
            className="w-full text-center font-mono text-lg font-bold text-gray-900 bg-gray-50 border-2 border-gray-200 rounded px-3 py-2 focus:border-blue-500 focus:outline-none"
          />
          {ultimaLeitura && codigoBarras && (
            <p className="text-xs text-gray-600 mt-2 text-center">
              <span className="font-semibold">{ultimaLeitura}</span>
            </p>
          )}
        </div>

        {/* Scan button */}
        <button
          onClick={onSimularLeitura}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-lg font-semibold mb-4 flex items-center justify-center gap-2"
        >
          <Scan className="w-5 h-5" />
          📷 Escanear Código
        </button>

        {/* Scanned items list */}
        {itensEscaneados.length > 0 && (
          <div className="bg-white rounded-lg p-3 mb-4 border-2 border-green-400">
            <p className="text-xs font-semibold text-gray-600 mb-3">
              ITENS ESCANEADOS ({itensEscaneados.length})
            </p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {itensEscaneados.map((item) => (
                <div
                  key={item.id}
                  className="bg-gray-50 border border-gray-200 rounded p-2 flex items-center gap-2"
                >
                  <div className="flex-1">
                    <p className="text-sm font-bold text-gray-900">{item.nome}</p>
                    <p className="text-xs text-gray-500">{item.unidade}</p>
                  </div>
                  <input
                    type="number"
                    value={item.quantidade}
                    onChange={(e) => onEditarQuantidade(item.id, parseInt(e.target.value) || 1)}
                    min="1"
                    className="w-16 text-center text-lg font-bold text-blue-600 bg-white border border-gray-300 rounded px-2 py-1"
                  />
                  <button
                    onClick={() => onRemoverItem(item.id)}
                    className="text-red-600 hover:text-red-700 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Confirm button */}
        {itensEscaneados.length > 0 && (
          <button
            onClick={onConfirmarItens}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-lg font-semibold mb-3 flex items-center justify-center gap-2"
          >
            ➕ Adicionar {itensEscaneados.length > 1 ? 'Itens' : 'Item'} ({itensEscaneados.reduce((acc, item) => acc + item.quantidade, 0)} un)
          </button>
        )}

        {/* Close button */}
        <button
          onClick={onClose}
          className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 rounded-lg font-medium"
        >
          Fechar Scanner
        </button>

        <p className="text-xs text-gray-500 mt-4 text-center">
          💡 Escaneie os produtos, edite as quantidades e clique em Adicionar
        </p>
      </div>
    </div>
  );
};
